import React from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { currentUserSelector, myOrganizationsSelector } from 'features/auth/authSlice'
import { eventSelector } from 'features/events/eventSlice'
import { addModal } from 'features/modals/modalSlice'
import { _, colors } from 'utils'
import { getNodes } from 'graphql/utils'
import {
    GetMyEventsQuery,
    GetMyEventsQueryVariables,
} from 'generated/graphql'
import { useQuery } from 'hooks'
import env from 'env'
import CopyToClipboard from 'react-copy-to-clipboard'
import useRoute from 'hooks/useRoute'
import { showNotification } from 'features/notifications'
import constants from 'constants/constants'
import LogoPlaceholder from 'assets/Logo_placeholder.png'
import Button from '../../Button'
import SwitchEventModal from './SwitchEventModal'
import { myEventsQuery } from '../query'
import UserInfo from './UserInfo'
import { MOBILE_BREAKPOINT, scaleSidebar } from '../Layout'
import HelpIcon from './HelpIcon'

export const S = {
    NavBar: styled.div`
        position: fixed;
        top: 0;
        left: 0;
        z-index: 100;
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        height: 60px;
        padding: 10px 20px;
        background: #FFFFFF;
        box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.1);
    `,
    LogoWrapper: styled.div<{ screenWidth: number }>`
        width: 230px;
        height: 100%;
        @media (min-width: 1441px) {
            width: ${({ screenWidth }) => scaleSidebar(screenWidth) - 20}px;
        }

    `,
    NavLogo: styled.img`
        width: 100%;
        height: 100%;
        object-fit: contain;
        object-position: center;
    `,
    Divider: styled.div`
        width: 0;
        height: 28px;
        border-left: 1px solid ${colors.iconOpacity(0.1)};
    `,
    EventInfoWrapper: styled.div`
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-left: 32px;
        padding-right: 16px;
    `,
    EventInfo: styled.div`
        font-weight: 300;
        color: ${colors.grayTitleOpacity(0.8)};
    `,
    EventActions: styled.div`
        & button {
            margin-left: 10px;
            color: ${colors.icon};
        }
        & > a {
            text-decoration: none;
        }
    `,
    ClipboardText: styled(Button)`
        &:hover {
            background-color: hsl(120, 100%, 93%) !important;
            color: black !important;
        }
    `
}

const TopNav = ({ logo, screenWidth }: { logo: string, screenWidth?: number }) => {
    const dispatch = useDispatch()
    const user = useSelector(currentUserSelector)
    const eventData = useSelector(eventSelector)
    const { data, fetchData } = useQuery<
        GetMyEventsQuery, GetMyEventsQueryVariables
    >(myEventsQuery, { first: 99 }, null, !user)
    const isSuperUser = data?.currentUser?.isSuperUser
    const currentRoute = useRoute()
    const context = currentRoute?.context
    const myOrganizations = useSelector(myOrganizationsSelector)

    const getEventPreviewUrl = () => {
        let url = constants.isLite
            ? `https://${eventData?.organization?.subdomain}.evd.events/${eventData.slug}`
            : `https://${env.url}${eventData.slug}`

        if (eventData.published) {
            return url
        }

        url += '/backstage'
        return url
    }

    const showCopiedMessage = () => {
        dispatch(showNotification({
            body: 'Copied',
            type: 'success',
            displayDuration: 1500
        }))
    }
    const showSwitchEventModal = () => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <SwitchEventModal
                    events={getNodes(data?.myEvents)}
                    closeListModal={close}
                    fetchData={fetchData}
                    organizations={myOrganizations}
                />
            ),
            header: _('switch_event.modal_header'),
        }))
    }

    if (screenWidth < MOBILE_BREAKPOINT) {
        return (
            <S.NavBar>
                <S.LogoWrapper screenWidth={screenWidth}>
                    <S.NavLogo src={logo} />
                </S.LogoWrapper>
            </S.NavBar>
        )
    }

    return (
        <S.NavBar>
            <S.LogoWrapper screenWidth={screenWidth}>
                <S.NavLogo src={logo || LogoPlaceholder} />
            </S.LogoWrapper>
            {eventData ? (
                <>
                    <S.Divider />
                    <S.EventInfoWrapper>
                        <S.EventInfo>
                            {eventData.name}
                        </S.EventInfo>
                        <S.EventActions>
                            {isSuperUser && context && (
                                <CopyToClipboard text={context || ''} onCopy={showCopiedMessage}>
                                    <S.ClipboardText variant="secondary" icon="clipboard">
                                        {context}
                                    </S.ClipboardText>
                                </CopyToClipboard>
                            )}
                            <Button variant="secondary" onClick={showSwitchEventModal}>
                                {_('top_navigation.switch_event')}
                            </Button>
                            <a
                                href={getEventPreviewUrl()}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <Button variant="secondary">
                                    {_('top_navigation.preview')}
                                </Button>
                            </a>
                        </S.EventActions>
                    </S.EventInfoWrapper>
                    <S.Divider />
                </>
            ) : <div className="flex-grow-1" />}
            <UserInfo user={user} isReseller={eventData?.currentUserHasResellerAccess} />
            <HelpIcon />
        </S.NavBar>
    )
}

export default TopNav
